// Client-side bot-protection helpers for the React forms (anonymous QR
// creation, email-capture). Mirrors the server-side checks in
// app/services/bot_protection/*.rb — see the Bot Protection playbook.
//
// Three signals are sent on every protected POST:
//   - honeypot field ("company") — must be empty
//   - form_started_at (Unix seconds) — must be >= 1s before submit
//   - cf-turnstile-response (token) — verified by Cloudflare when configured
//
// When TURNSTILE_SITE_KEY is unset, the server fails open (graceful
// degradation) and the JS-side widget renders nothing — local dev / CI
// keep working unchanged.
//
// Honeypot field name. Kept in one place so the React forms and the ERB
// contact form use the same key.
export const HONEYPOT_FIELD = "company"

export function turnstileSiteKey() {
  return document.querySelector('meta[name="turnstile-site-key"]')?.content || null
}

// Returns the current Unix-second timestamp, suitable for embedding as the
// form_started_at hidden field at mount time.
export function nowEpochSeconds() {
  return Math.floor(Date.now() / 1000)
}

// BotProtection::TimingCheck::MIN_SECONDS, from the meta tag the layouts
// render (shared/_turnstile.html.erb), so the client waits on the server's own
// number. 0 (no wait) if the tag is missing; a test holds both layouts to it.
export function minFormSeconds() {
  const value = parseFloat(document.querySelector('meta[name="bot-min-form-seconds"]')?.content)
  return Number.isFinite(value) && value > 0 ? value : 0
}

// Milliseconds until a form stamped at `startedAt` passes the server's timing
// check. The server compares whole seconds (`Time.now.to_i - started_at`), and
// the stamp is already floored, so the form is old enough at the first whole
// second at or past the minimum: 2 s after the stamp for a 1.5 s minimum.
export function msUntilFormOldEnough(startedAt, nowMs = Date.now()) {
  const min = minFormSeconds()
  if (!min) return 0
  return Math.max(0, (startedAt + Math.ceil(min)) * 1000 - nowMs)
}

// Under BotProtection::TimingCheck::MAX_SECONDS (24h), so a form that has been
// open too long is re-stamped *before* the server would start refusing it. A
// backgrounded landing page routinely outlives a day; without this it fails the
// timing signal on every submit, forever, with no way for the visitor out.
export const STALE_FORM_SECONDS = 23 * 60 * 60

// The stamp to submit for a form opened at `startedAt`: the original, or a
// fresh one if it has gone stale. Deliberately *not* an unconditional
// re-stamp — TimingCheck also has a MIN_SECONDS floor, and restarting the
// clock on every retry would trade the stale dead end for a too-fast one.
export function unstaleFormStart(startedAt) {
  const now = nowEpochSeconds()
  return now - startedAt >= STALE_FORM_SECONDS ? now : startedAt
}
